/**
 * Activity: change history feed across vaults and secure items.
 */
import { createRoute } from "@tanstack/solid-router";
import { msg } from "@lingui/core/macro";
import { For, Show } from "solid-js";
import { History } from "@fleet/icons";
import { ACTIVITY_FEED, type ActivityRow } from "@fleet/plugins/activity";
import { useKernelService } from "@fleet/kernel";
import { appRoute } from "./route";
import { PageContainer, Skeleton } from "@fleet/ui";
import { EmptyState } from "@/components/ui/empty-state";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/providers/i18n-provider";
import { ActivityItem } from "@/features/activity/components/activity-item";
import {
  activitySentence,
  formatRelativeTime,
  groupActivityByBatch,
  isOfflineEdit,
} from "@/features/activity/lib/activity-text";

function ActivityPage() {
  const { t, locale } = useI18n();
  const feed = useKernelService(ACTIVITY_FEED);

  const rows = (): ActivityRow[] => feed()?.rows() ?? [];

  return (
    <PageContainer maxWidth="2xl" class="flex flex-col gap-4 pb-16">
      <header class="flex flex-col gap-1 pt-2">
        <h1 class="flex items-center gap-2 text-xl font-bold text-foreground">
          <History class="h-5 w-5 text-muted-foreground" />
          {t(msg`Activity`)}
        </h1>
        <p class="text-xs text-muted-foreground">
          {t(msg`A private audit log of changes made to your vaults and secrets.`)}
        </p>
      </header>

      <Show
        when={!(feed()?.isLoading() ?? true)}
        fallback={
          <div class="flex flex-col gap-3">
            <Skeleton class="h-16 w-full rounded-2xl" />
            <Skeleton class="h-16 w-full rounded-2xl" />
            <Skeleton class="h-16 w-full rounded-2xl" />
          </div>
        }
      >
        <Show
          when={rows().length > 0}
          fallback={
            <div data-testid="activity-empty" class="flex flex-1">
              <EmptyState
                icon={History}
                title={t(msg`No activity yet`)}
                description={t(
                  msg`As you create vaults and save items, your history will appear here.`,
                )}
              />
            </div>
          }
        >
          <section class="flex flex-col gap-2" data-testid="activity-list">
            <For each={groupActivityByBatch(rows())}>
              {(group) => (
                <For each={group.entries}>
                  {(row) => {
                    const { descriptor, values } = activitySentence(row);
                    return (
                      <ActivityItem
                        sentence={t(descriptor, values)}
                        timeLabel={formatRelativeTime(row.server_ts, locale())}
                        native={row.platform === "native"}
                        offline={isOfflineEdit(row)}
                        offlineLabel={t(msg`Done offline`)}
                        revertible={false}
                        reverting={false}
                        undoLabel={t(msg`Undo`)}
                        onRevert={() => {}}
                      />
                    );
                  }}
                </For>
              )}
            </For>
          </section>

          <Show when={feed()?.hasMore()}>
            <div class="flex justify-center pt-2">
              <Button
                variant="outline"
                size="sm"
                disabled={feed()?.isLoadingOlder()}
                onClick={() => void feed()?.loadOlder()}
                data-testid="activity-load-older"
              >
                {feed()?.isLoadingOlder() ? t(msg`Loading…`) : t(msg`Load older activity`)}
              </Button>
            </div>
          </Show>
        </Show>
      </Show>
    </PageContainer>
  );
}

export const activityRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/activity",
  component: ActivityPage,
});
